import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Calendar, MapPin, Wrench, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalizedPath } from "@/i18n/useLocalizedPath";
import { fadeUp, stagger, viewportOnce } from "./motion-presets";

const STEPS: Array<{ key: string; icon: LucideIcon }> = [
  { key: "schedule", icon: Calendar },
  { key: "dispatch", icon: Wrench },
  { key: "watch", icon: MapPin },
];

export const HowItWorks = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("landing");
  const lp = useLocalizedPath();
  return (
    <section id="how-it-works" className="relative py-24 px-4">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
        className="container mx-auto max-w-6xl"
      >
        <div className="text-center max-w-2xl mx-auto">
          <motion.span
            variants={fadeUp}
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {t("howItWorks.eyebrow")}
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-3 font-display text-3xl md:text-5xl font-semibold tracking-tight text-foreground"
          >
            {t("howItWorks.title")}
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-muted-foreground">
            {t("howItWorks.subtitle")}
          </motion.p>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {STEPS.map((item, i) => (
            <motion.div key={item.key} variants={fadeUp} className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10 border border-primary/15">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground tabular-nums">
                  {t("howItWorks.stepLabel", { n: i + 1 })}
                </span>
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground">{t(`howItWorks.steps.${item.key}.title`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`howItWorks.steps.${item.key}.description`)}</p>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fadeUp} className="mt-14 text-center">
          <Button
            onClick={() => navigate(lp("/auth") + "?mode=signup")}
            className="btn-gradient text-white border-0 h-10 px-5 gap-2"
          >
            {t("howItWorks.cta")} <ArrowRight className="h-4 w-4" />
          </Button>
        </motion.div>
      </motion.div>
    </section>
  );
};
